import { DeleteForever, MoreVert } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
	Avatar,
	Box,
	CardHeader,
	Divider,
	Grid,
	ListItemIcon,
	ListItemText,
	MenuItem,
	Tab,
	Tabs,
	Typography
} from "@mui/material";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import { WithStyles, WithTheme } from "@mui/styles";
import withStyles from "@mui/styles/withStyles";
import { AppState } from "AppState";
import { AxiosError, AxiosResponse } from "axios";
import { Cluster } from "pages/management/cluster/types";
import { hostListFetchRequested } from "store/host/actions";
import StrayHostFormComponent from "pages/management/host/hostForm/HostFormComponent";
import { Host, HOST_STATE } from "pages/management/host/types";
import HostUtils from "pages/management/host/utils";
import JobListComponent from "pages/management/node/jobs/JobListComponent";
import { BlinkingBadge } from "components/BlinkingBadge/BlinkingBadge";
import { GMDialogService } from "components/dialog/DialogService";
import HostStateComponent from "components/monitoring/hostState/HostStateComponent";
import HostLogTable from "components/logViewer/HostLogTableComponent";
import { showSnackbar } from "components/snackbar/actionCreators";
import {
	SNACKBAR_TYPE,
	SnackbarActionPayload
} from "components/snackbar/types";
import { Server } from "mdi-material-ui";
import HostsAPI from "services/api/HostsAPI";
import LogsApi from "services/api/LogsApi";
import JobsService from "services/jobs/jobsService";
import { Job, JOB_STATUS, JobTracking } from "services/jobs/types";
import React, { ChangeEvent } from "react";
import { connect } from "react-redux";
import { StaticContext } from "react-router";
import { RouteComponentProps } from "react-router-dom";
import { createSelector } from "reselect";
import { styles } from "pages/management/host/styles";

// component local state interface
interface LocalState {
	host: Host;
	activeTab: number;
	logs: any[];
	anchorEl: any;
}

interface LocalProps {}

// PROPS
interface ReduxStateProps {
	host: Host;
	cluster: Cluster;
	hasRunningJobs: boolean;
}

interface ReduxDispatchProps {
	showSnackbar: (snackbar: SnackbarActionPayload) => void;
	reloadHosts: (clusterID: number) => void;
}

type Props = LocalProps &
	ReduxStateProps &
	ReduxDispatchProps &
	WithStyles<typeof styles> &
	WithTheme &
	RouteComponentProps<any, StaticContext, any>;

interface Snapshot {
	hasHostChanged: boolean;
}

// COMPONENT
class HostManagerComponent extends React.Component<Props, LocalState> {
	_isMounted = false;

	_cancelRequest: any;
	_scheduledFetch: any;

	constructor(props: Props) {
		super(props);

		// console.log("props", props);

		if (props.host) {
			this.state = {
				host: props.host,
				activeTab: 0,
				logs: [],
				anchorEl: undefined
			};
			this.getLogs();
		} else {
			this.props.showSnackbar({
				msg: `Error loading host data`,
				snackbarType: SNACKBAR_TYPE.ERROR
			});
		}
	}

	getLogs = () => {
		this.props.host &&
			LogsApi.fetchHostLog(this.props.host).then((response: AxiosResponse) => {
				console.log("deployment logs", response.data);

				const splitLogs = response.data.data.attributes.contents.split("\n");
				// console.log("split logs", splitLogs);

				this.setState((state: LocalState) => ({
					...state,
					logs: splitLogs.reduce((result: any, row: string) => {
						// console.log("raw line", row);

						try {
							const line = JSON.parse(row);

							// console.log("line", line);

							result.push({
								msg: `${line.time} | ${line.msg}`
							});
						} catch (e) {
							// console.warn("Parsing log line failed", e);
						}

						return result;
					}, [])
				}));
			});
	};

	getSnapshotBeforeUpdate(
		prevProps: Readonly<Props>,
		prevState: Readonly<LocalState>
	): any | null {
		return {
			hasHostChanged:
				prevProps.match.params.hostID !== this.props.match.params.hostID
		};
	}

	componentDidUpdate(
		prevProps: Readonly<Props>,
		prevState: Readonly<LocalState>,
		snapshot?: Snapshot
	): void {
		if (snapshot && snapshot.hasHostChanged) {
			const { host } = this.props;

			if (host) {
				this.setState(() => ({
					host,
					logs: [],
					anchorEl: undefined
				}));
				this.getLogs();
				this.clearAsyncRequests();
			} else {
				this.props.showSnackbar({
					msg: `Error loading data.`
				});
			}
		}
	}

	componentDidMount(): void {
		this._isMounted = true;
	}

	componentWillUnmount(): void {
		this._isMounted = false;

		this.clearAsyncRequests();
	}

	clearAsyncRequests(): void {
		if (this._cancelRequest) this._cancelRequest();
		if (this._scheduledFetch) clearTimeout(this._scheduledFetch);
	}

	onDeleteClick = (host: Host, force?: boolean): void => {
		const message = force
			? `Host will be deleted if possible. If deletion fails, the host will still be removed from Galera Manager.`
			: `Host will be deleted.`;

		GMDialogService.showConfirm({
			title: force
				? `Force delete host ${this.state.host.name}?`
				: `Delete host ${this.state.host.name}?`,
			message,
			confirmText: force ? "Force delete" : "Delete",
			declineText: "Cancel",
			destructiveConfirm: true
		}).then(
			() => {
				HostsAPI.delete(host, force).then(
					(job: Job) => {
						console.log("host delete job", job);

						JobsService.trackJob(job).then(
							() => {
								this.props.showSnackbar({
									msg: `Host '${host.name}' successfully deleted`
								});

								// redirect to cluster if host still open
								if (
									this.props.location.pathname.endsWith(
										`/clusters/${host.clusterID}/hosts/${host.id}`
									)
								) {
									this.props.history.push(`/clusters/${host.clusterID}`);
								}

								this.props.reloadHosts(host.clusterID);
							},
							(err: any) => {
								console.error("Host deletion job monitor error:", err);

								this.props.showSnackbar({
									msg: `Host '${host.name}' deletion job error.`,
									snackbarType: SNACKBAR_TYPE.ERROR
								});
							}
						);
					},
					(err: AxiosError) => {
						console.error("Error deleting host:", err);

						this.props.showSnackbar({
							msg: `Host '${host.name}' could not be deleted`,
							snackbarType: SNACKBAR_TYPE.ERROR
						});
					}
				);

				this.props.showSnackbar({
					msg: `Host '${host.name}' is being deleted`
				});
			},
			() => {}
		);

		this.setState({
			anchorEl: null
		});
	};

	onMoreClick = (event: any) => {
		// isExpanded menu
		this.setState({ ...this.state, anchorEl: event.currentTarget });
	};

	onMenuClose = () => {
		this.setState({ ...this.state, anchorEl: null });
	};

	render(): React.ReactNode {
		const { classes, theme, hasRunningJobs, cluster } = this.props;

		if (!this.state) return false;

		const { host, activeTab, anchorEl } = this.state;

		const hostState: HOST_STATE = HostUtils.getHostState(host.deploymentStatus);

		return (
			<Card className={classes.card}>
				<CardHeader
					avatar={
						<Avatar
							style={{ backgroundColor: theme.palette.primary.main }}
							aria-label="Host"
						>
							<Server />
						</Avatar>
					}
					action={
						<Grid container direction="row" alignItems="center">
							<Grid item>{<HostStateComponent hostState={hostState} />}</Grid>
							<Grid item>
								<IconButton
									data-testid="host-manager-more-button"
									onClick={this.onMoreClick}
									size="large"
								>
									<MoreVert />
								</IconButton>
								<Menu
									id="long-menu"
									anchorEl={anchorEl}
									open={Boolean(anchorEl)}
									onClose={this.onMenuClose}
								>
									<MenuItem
										data-testid="delete-host"
										onClick={() => {
											this.onDeleteClick(host);
										}}
									>
										<ListItemIcon>
											<DeleteIcon />
										</ListItemIcon>
										<ListItemText primary="Delete host" />
									</MenuItem>
									<MenuItem
										data-testid="delete-host-force"
										onClick={() => {
											this.onDeleteClick(host, true);
										}}
									>
										<ListItemIcon>
											<DeleteForever />
										</ListItemIcon>
										<ListItemText primary="Force delete host" />
									</MenuItem>
								</Menu>
							</Grid>
						</Grid>
					}
					title={host.name}
					subheader={host.system}
				/>
				<Divider style={{ margin: 0 }} />
				<Tabs
					value={activeTab}
					onChange={(event: ChangeEvent<{}>, newValue: any) => {
						this.setState((state: LocalState) => ({
							...state,
							activeTab: newValue
						}));
					}}
					variant="fullWidth"
				>
					<Tab label={<Typography>Logs</Typography>} />
					<Tab
						label={
							<Typography data-testid="configuration-tab">Configuration</Typography>
						}
					/>
					<Tab
						label={
							hasRunningJobs ? (
								<BlinkingBadge variant="dot" color="primary">
									<Typography>Jobs</Typography>
								</BlinkingBadge>
							) : (
								<Typography>Jobs</Typography>
							)
						}
					/>
				</Tabs>
				<CardContent>
					{(activeTab === 0 && (
						<Box style={{ height: 500 }}>
							<HostLogTable
								rowCount={this.state.logs.length}
								rowGetter={({ index }: any) => this.state.logs[index]}
								columns={[
									{
										width: 200,
										flexGrow: 1,
										label: "",
										dataKey: "msg"
									}
								]}
							/>
						</Box>
					)) ||
						(activeTab === 1 && (
							<StrayHostFormComponent cluster={cluster} host={host} />
						)) ||
						(activeTab === 2 && <JobListComponent host={host} />)}
				</CardContent>
			</Card>
		);
	}
}

// selectors
const makeHostSelector = () =>
	createSelector(
		(state: AppState) => state.hostList,
		(state: AppState, props: Props) => props.match.params.clusterID,
		(state: AppState, props: Props) => props.match.params.hostID,
		(hostMap: Map<number, Host[]>, clusterID: number, hostID: number): Host => {
			const hostList = hostMap.get(clusterID) || [];

			const host = hostList.find((host: Host) => host.id === hostID);
			if (!host) throw Error(`Could not find host`);

			return host;
		}
	);

const makeClusterSelector = () =>
	createSelector(
		(state: AppState) => state.clusterList,
		(state: AppState, props: Props) => props.match.params.clusterID,
		(clusterList: Cluster[], clusterID: number): Cluster => {
			const cluster = clusterList.find(
				(cluster: Cluster) => cluster.id === clusterID
			);

			if (!cluster) throw Error(`Could not find cluster`);

			return cluster;
		}
	);

const makeHasRunningJobs = () =>
	createSelector(
		(state: AppState) => state.jobMonitor.runningJobList,
		(state: AppState, props: Props) => props.match.params.clusterID,
		(state: AppState, props: Props) => props.match.params.hostID,
		(jobList: JobTracking[], clusterId: number, hostId: number) =>
			jobList.some(
				(job: JobTracking) =>
					job.meta.cluster_id === clusterId &&
					job.meta.host_id === hostId &&
					job.status === JOB_STATUS.RUNNING
			)
	);

// REDUX MAPPINGS
const mapGlobalStateToProps = (state: AppState, props: Props) => {
	const hostSelector = makeHostSelector();
	const clusterSelector = makeClusterSelector();
	const hasRunningJobsSelector = makeHasRunningJobs();

	return {
		host: hostSelector(state, props),
		cluster: clusterSelector(state, props),
		hasRunningJobs: hasRunningJobsSelector(state, props)
	};
};

const mapGlobalDispatchToProps = (dispatch: any) => {
	return {
		reloadHosts: (clusterID: number) => {
			dispatch(hostListFetchRequested(clusterID));
		},
		showSnackbar: (snackbar: SnackbarActionPayload) => {
			dispatch(showSnackbar(snackbar));
		}
	};
};

export default withStyles(styles, { withTheme: true })(
	connect(mapGlobalStateToProps, mapGlobalDispatchToProps)(HostManagerComponent)
);
